import React from "react";
import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay";

import { faArrowDown } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import cx from "classnames";

import { showFeatureModal, UpgradeIcon } from "~/billing";
import { withSuspense } from "~/lib/suspense";
import { reduceEdgesToNodes } from "~/lib/utils";
import { SummaryText } from "~/meetingRecorder/SummaryText";
import InlineButton, { LabelIconForInlineButton } from "~/ui/button/InlineButton";

import { MeetingDate } from "./MeetingHeader/MeetingDateOrTimer";
import openMeetingModal from "./MeetingModal/openMeetingModal";
import { usePaywallCheck } from "./utils";

import type {
	PreviousMeetings_stream$data,
	PreviousMeetings_stream$key,
} from "~/__generated__/PreviousMeetings_stream.graphql";
import type { PreviousMeetings_streamPaginationQuery as StreamPaginationQuery } from "~/__generated__/PreviousMeetings_streamPaginationQuery.graphql";
import type { PreviousMeetingsQuery } from "~/__generated__/PreviousMeetingsQuery.graphql";

import styles from "./PreviousMeetings.module.css";

const streamFragment = graphql`
	fragment PreviousMeetings_stream on NoteStream
	@argumentDefinitions(
		before: { type: "String" }
		after: { type: "String" }
		first: { type: "Int", defaultValue: 4 }
		last: { type: "Int" }
		startFromNote: { type: "ID" }
	)
	@refetchable(queryName: "PreviousMeetings_streamPaginationQuery") {
		id
		notes(first: $first, after: $after, last: $last, before: $before, startFromNote: $startFromNote)
			@connection(key: "PreviousMeetings_notes") {
			edges {
				node {
					id
					createdAt
					eventStart
					eventEnd
					title
					url
					event {
						start
						end
						isAllDay
					}
					meetingRecordings {
						id
						activeSummary {
							summary
						}
						meetingRecordedVideoPosterUrl
					}
				}
			}
		}
	}
`;

const query = graphql`
	query PreviousMeetingsQuery($noteId: ID!) {
		account {
			billingAccount {
				hasAccessToHistory: hasAccessToFeature(feature: history)
				historyLimitInDays: usageLimitForFeature(feature: history)
			}
		}
		note: node(id: $noteId) {
			... on Note {
				id
				stream {
					id
					...PreviousMeetings_stream @arguments(startFromNote: $noteId)
				}
			}
		}
	}
`;

type StreamNote = NonNullable<NonNullable<NonNullable<PreviousMeetings_stream$data["notes"]>["edges"][number]>["node"]>;

function getMeetingEventData(note: StreamNote) {
	// Use event data if available, otherwise fall back to eventStart/eventEnd
	const start = note.event?.start || note.eventStart;
	const end = note.event?.end || note.eventEnd;

	if (!start || !end) return null;

	return {
		start: new Date(start),
		end: new Date(end),
		isAllDay: note.event?.isAllDay ?? false,
	};
}

interface PreviousMeetingCardProps {
	note: StreamNote;
	hasAccessToHistory: boolean;
	historyLimitInDays: number | null;
}

function PreviousMeetingCard({ note, hasAccessToHistory, historyLimitInDays }: PreviousMeetingCardProps) {
	// Find the first recording with an active summary (if any)
	const recording = note.meetingRecordings?.find(r => r.activeSummary?.summary);

	// Ensure we aren't creating a Date with an empty string
	const createdAt = note.createdAt !== "" && note.createdAt !== null ? new Date(note.createdAt) : null;
	const meetingEvent = getMeetingEventData(note);

	const isPaywalled = usePaywallCheck(note.createdAt, hasAccessToHistory, historyLimitInDays);

	const openModal = React.useCallback(() => {
		if (isPaywalled) {
			showFeatureModal("history");
			return;
		}
		openMeetingModal({ noteId: note.id });
	}, [note.id, isPaywalled]);

	return (
		<div
			key={note.id}
			className={cx(styles.meetingRow, { [styles.withVideo]: !!recording?.meetingRecordedVideoPosterUrl })}
			onClick={openModal}
		>
			<div className={styles.meetingInfo}>
				<div className={styles.meetingHeader}>
					<h3 className={styles.meetingTitle}>{note.title}</h3>
					<p className={styles.meetingTime}>
						<MeetingDate createdAt={createdAt} isCancelled={false} event={meetingEvent} />
					</p>
					{isPaywalled && <UpgradeIcon />}
				</div>

				{recording?.activeSummary?.summary && (
					<SummaryText className={styles.meetingSummary}>{recording.activeSummary.summary}</SummaryText>
				)}
			</div>

			{recording?.meetingRecordedVideoPosterUrl && (
				<img className={styles.meetingThumbnail} src={recording.meetingRecordedVideoPosterUrl} alt="Recap thumbnail" />
			)}
		</div>
	);
}

interface PreviousMeetingsProps {
	noteId: string;
}

function PreviousMeetings({ noteId }: PreviousMeetingsProps) {
	const data = useLazyLoadQuery<PreviousMeetingsQuery>(
		query,
		{
			noteId,
		},
		{
			fetchPolicy: "store-and-network",
		},
	);

	const {
		data: streamData,
		loadNext,
		isLoadingNext,
		hasNext,
	} = usePaginationFragment<StreamPaginationQuery, PreviousMeetings_stream$key>(
		streamFragment,
		data.note?.stream ?? null,
	);

	const {
		account: {
			billingAccount: { hasAccessToHistory, historyLimitInDays },
		},
	} = data;

	const loadMore = React.useCallback(() => {
		loadNext(5);
	}, [loadNext]);

	const notes = reduceEdgesToNodes(streamData?.notes?.edges).filter(n => n && n.id !== noteId);

	if (notes.length === 0) {
		return null;
	}

	return (
		<div className={styles.previousMeetingsContainer}>
			<div className={styles.header}>Previous meetings</div>
			<div className={styles.meetingsContainer}>
				{notes.map(note => (
					<PreviousMeetingCard
						key={note.id}
						note={note}
						hasAccessToHistory={hasAccessToHistory}
						historyLimitInDays={historyLimitInDays}
					/>
				))}
			</div>

			{hasNext && (
				<InlineButton className={styles.loadMoreButton} onClick={loadMore} disabled={isLoadingNext}>
					{isLoadingNext ? (
						"Loading..."
					) : (
						<>
							Load more
							<LabelIconForInlineButton icon={faArrowDown} side="right" />
						</>
					)}
				</InlineButton>
			)}
		</div>
	);
}

function LoadingFallback() {
	return (
		<div className={styles.previousMeetingsContainer}>
			<div className={styles.header}>Previous meetings</div>
			<div className={styles.meetingsContainer}>
				{Array.from({ length: 3 }).map((_, index) => (
					// eslint-disable-next-line react/no-array-index-key
					<div key={index} className={styles.skeleton}>
						<div className={styles.skeletonTitle}></div>
					</div>
				))}
			</div>
		</div>
	);
}

export default withSuspense(<LoadingFallback />, PreviousMeetings);
